import React from 'react';
import Utils from '../../common/Utils';
import HTML from '../../common/HtmlHelpers';
import {MiscEndpoints, TrackerEndpoints, TractorOwnerEndpoints} from '../../common/ProxyEndpoints';
import SessionStateManager from '../../common/SessionStateManager';
import CardTile from "../../components/CardTile/CardTile";
import StatisticsChart from "../../components/StatisticsChart/StatisticsChart";
import TTLProxy from "../../common/TTLAsyncProxy";
import {getTimeFrameRange} from './DashboardCommon';
import DocumentTitle from 'react-document-title';
import AppVariables from '../../common/AppVariables';
import MapBase from "../Maps/MapBase";
import PropTypes from "prop-types";
import PermissionRender from "../../components/PermissionRender/PermissionRender";
import {Permissions} from "../../common/Constants";

const brandInfo = '#63c2de';

/*
* The request view mode
* */
const RequestTimeFrame = Object.freeze({
    Day:   'day',
    Week: 'week',
    Month: 'month' ,
    Year:  'year',
    Custom: 'custom'
});

/**
 * Map Mini Component
 * @param props
 * @return {XML}
 * @constructor
 */
const MapMini = (props) => {

    const proxy = TTLProxy.getInstance();

    function getOwnerId(){
        return proxy.getUserInfo().owner.id;
    }

    function getTractors(options){
        return proxy.processRequest( TractorOwnerEndpoints.getTractors(getOwnerId()).url , options ).then(response => response.getPayload());
    }

    function getLocations(tractors){
        return proxy.processRequest( TrackerEndpoints.getTrackersLocation() ).then(response => response.getPayload());
    }


    return <MapBase getTractors={getTractors.bind(this)}
                    mapHeight={props.mapHeight}
                    showAddress={false}
                    getLocations={getLocations.bind(this)}/>

};

MapMini.propTypes = {
    mapHeight: PropTypes.number
};

/**
 * Dashboard - Tractor Owner View
 */
export default class DashboardOwner extends React.Component {

    constructor(props){

        super(props);

        const session = new SessionStateManager('owner_dashboard', false );

        this.session = session;
        this.proxy = TTLProxy.getInstance();

        this.state = {
            isLoaded: session.getState('isLoaded',false),
            errorMessage: null,
            faulted: session.getState('faulted',false),
            tooltipState:  {},
            dashboardStats:session.getState('dashboardStats', {
                totalTractors: 0,
                totalOperators: 0,
                totalAcreage: 0,
                totalHectarage: 0,
                totalIncome: 0,
                pendingRequests: 0
            }),
            acreageStats: session.getState('acreageStats',{
                time_frame: RequestTimeFrame.Day,
                completed_requests:[],
            }),
            trackers: null
        };

        //
        this._isMounted = false;
    }


    setStateSafe(state,callback){

        if(this._isMounted){
            this.setState(state,callback);
        }

    }

    fetchTractorsLocation(){

        const proxy = this.proxy;
        return proxy.processRequest( TrackerEndpoints.getAll() ).then(response => {

            //
            this.setStateSafe({
                trackers: response.getPayload()
            });

        });

    }

    componentWillUnmount(){

        //
        this._isMounted = false;

        this.session.putStateFrame({
            isLoaded: this.state.isLoaded,
            faulted: this.state.faulted,
            dashboardStats: this.state.dashboardStats,
            acreageStats: this.state.acreageStats
        });

        this.session.save();
    }

    getOwnerId(){
        return this.proxy.getUserInfo().owner.id;
    }

    enterErrorState(err){

        if(this._isMounted) {
            HTML.toastWarning(Utils.getResponseError(err));
            this.setStateSafe({faulted: true});
        }

    }

    fetchAcreageStats(timeFrame){

        const me = this;
        timeFrame = timeFrame === undefined ? this._acreageChart.getTimeFrame() : timeFrame;
        return this.proxy.processRequest(TractorOwnerEndpoints.getAcreageStats(this.getOwnerId(), timeFrame, getTimeFrameRange(timeFrame) , 1  )).then((response) => {

            me.setStateSafe({
                acreageStats: response.getPayload(),
            });

            return response;
        });

    }

    navigateUrl(url , args){
        this.props.history.push(url,args);
    }

    queryData(){

        const proxy = this.proxy;
        const me = this;

        return Promise.all([

            //  get dashboard statistics
            proxy.processRequest(MiscEndpoints.getOwnerDashboardStats(this.getOwnerId())).then((response) =>{

                me.setStateSafe({
                    dashboardStats: response.getPayload(),
                });

                return response;
            }),

            //  fetch requests statistics
            me.fetchAcreageStats()

        ]).then((values) =>{

            if(me._isMounted) {
                me.setStateSafe({
                    isLoaded: true,
                    faulted: false
                });
            }

        }).catch((err) =>{
            me.enterErrorState(err);
        });

    }

    async componentDidMount(){

        this._isMounted = true;

        if(!this.state.isLoaded || !this.state.faulted) {
            await this.queryData();
        }

    }

    onAcreageStatsTimeFrameChanged(timeFrame){
        this.fetchAcreageStats(timeFrame);
    }

    render() {

        const stats = this.state.dashboardStats;
        return (<DocumentTitle title={"Tractor Owner Dashboard"}>
                <div>
                <div className="container-fluid">
                    <div className="animated fadeIn">
                        <div className="row content-frame">

                            <div className="col-sm-8 col-md-3">

                                <CardTile title="Days"
                                          icon="fa fa-tree"
                                          tooltipTitle="Days"
                                          tooltipContent="Shows total no. of days your tractors have completed for all requests"
                                          classes="card card-inverse card-info"
                                          value={stats.totalHectarage}
                                          navigateText="Click to see"
                                          onClick={() => this.navigateUrl('/requests?request_status=completed')} />

                            </div>

                            <div className="col-sm-8 col-md-3">

                                <CardTile title="Operators"
                                          icon="fa fa-users"
                                          tooltipTitle="Operators"
                                          tooltipContent="Indicates the total number of tractor operators registered on your behalf."
                                          classes="card card-inverse card-success"
                                          value={stats.totalOperators}
                                          navigateText="Click to see"
                                          onClick={() => this.navigateUrl('/operators')}/>

                            </div>

                            <div className="col-sm-8 col-md-3">

                                <CardTile title="Total Income"
                                          icon="fa fa-money"
                                          tooltipTitle="Income"
                                          tooltipContent={`Shows the total revenue ${AppVariables.CurrencyCode} generated from completed services.`}
                                          classes="card card-inverse card-warning"
                                          value={stats.totalIncome}
                                          preValue={AppVariables.CurrencyCode}/>

                            </div>

                            <div className="col-sm-8 col-md-3">

                                <CardTile title="Tractors"
                                          icon={<img src="/img/avatars/tractor_logo_white.png" width={34} height={34} />}
                                          tooltipTitle="Tractors"
                                          tooltipContent="Displays the total number tractors you posses."
                                          classes="card card-inverse card-primary"
                                          value={stats.totalTractors }
                                          navigateText="Click to see"
                                          onClick={() => this.navigateUrl('/tractors')}/>

                            </div>

                            <div className="card" style={{width: '100%'}}>
                                <div className="card-block">

                                    <StatisticsChart title="Hectarage (Request Statistics)"
                                                     titleResponsive="Hectarage"
                                                     data={this.state.acreageStats}
                                                     ref={(input) => this._acreageChart = input}
                                                     peakOffset={20}
                                                     sections={[
                                                         {
                                                             key: 'completed_requests',
                                                             label: 'Hectares',
                                                             backgroundColor: Utils.convertHex(brandInfo,10),
                                                             borderColor: brandInfo,
                                                             borderWidth: 2
                                                         }
                                                     ]}
                                                     onTimeFrameChanged={(frame) => this.fetchAcreageStats(frame)}
                                                     keepState={true}
                                                     sessionKey="acreage_chart_state"/>

                                </div>

                            </div>

                        </div>
                    </div>

                </div>

                <PermissionRender required={[ Permissions.User.MonitorTrackers ]}>
                    <div style={{padding: 20 , backgroundColor: "#f1efe9"}}>
                        <h6><i className="fa fa-map-marker"></i> Tractors Map View</h6>
                        <div style={{marginTop : 10}} >
                            <MapMini  mapHeight={480} />
                        </div>
                    </div>
                </PermissionRender>

            </div>
            </DocumentTitle>);
    }
}



// WEBPACK FOOTER //
// ./src/views/Dashboard/DashboardOwner.js